import { ColumnsType } from 'antd/es/table';
import { TFunction } from 'i18next';
import { AllMonitorsAPI } from 'jsx';
import {
  CompactTable,
  Flex,
  objectArraySort,
  PageContent,
  RowActionButton,
  useAssetParams,
  useFetch,
  usePageQuery,
  useUserinfo,
} from '@mss/mss-component-library';
import { Tooltip } from 'antd';
import { Copy } from 'react-feather';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams, useRouteMatch } from 'react-router-dom';
import dateFormat from 'dateformat';
import humanizeDuration from 'humanize-duration';
import JsonDetail from 'jsx/common/JsonDetail';
import StatusCircle from 'jsx/common/StatusCircle';
import { getMonitorDescriptionByName } from '../../../asset/util/monitorHelper';
import { sanitizedUrl } from '../../../asset/util/sanitize';

type AssetMonitorAPI = {
  id?: number;
  monitor_name?: string;
  last_run?: string | null;
  asset?: string;
  enabled: boolean;
};

interface EnrichedAssetMonitor extends AssetMonitorAPI, AllMonitorsAPI {
  enabled: boolean;
  events: any[];
}

type MonitorLog = {
  created_at?: string;
  state?: string;
  events?: LogEvent[];
  exception?: string;
};
type LogEvent = {
  entity?: string;
  value?: string;
};

const DATE_FORMAT = 'mmmm dS, yyyy @ h:MM:ss TT';
const PAGE_SIZE = 10;

const MonitorLogs = () => {
  const { assetId: id } = useAssetParams();
  const [, , user] = useUserinfo();

  const { monitorId } = useParams<{ monitorId: string }>();
  const [t] = useTranslation(['common', 'asset', 'applicationsSecurity']);
  const history = useHistory();
  const { url } = useRouteMatch();

  const [monitor, setMonitor] = useState<EnrichedAssetMonitor>();
  const parseMonitorData =
    () =>
    ({ data }: { data: AssetMonitorAPI[] }) => {
      data.forEach(x => {
        if (x?.id?.toString() === monitorId) {
          setMonitor(x as EnrichedAssetMonitor);
        }
      });
    };
  useFetch<AssetMonitorAPI[]>({
    url: `/api/automation/monitor-asset/?asset=${id}`,
    defaultValue: [],
    onSucces: parseMonitorData(),
  });

  const [monitorLogs, setMonitorLogs] = useState<MonitorLog[]>([]);
  const parseMonitorLogsData =
    () =>
    ({ data }: { data: MonitorLog[] }) => {
      setMonitorLogs(data);
    };
  useFetch<MonitorLog[]>({
    url: `/api/center/monitor/${monitorId}/logs/`,
    defaultValue: [],
    onSucces: parseMonitorLogsData(),
  });

  const goBackToMonitors = () => {
    history.push(
      `${sanitizedUrl(`${url.split('monitor-settings')[0]}monitor-settings`)}`
    );
  };

  const [modal, setModal] = useState<MonitorLog | null>(null);
  const [page, goToPage] = usePageQuery(monitorLogs.length / PAGE_SIZE);

  return (
    <PageContent
      title={
        <h4 className="mb-8">
          <span
            style={{ color: '#417e80', cursor: 'pointer' }}
            onClick={() => goBackToMonitors()}
          >
            {t('Monitor settings')}
          </span>
          <span className="ml-1 mr-1">/</span>
          <span>
            {getMonitorDescriptionByName(monitor?.monitor_name || '', t).name}
          </span>
        </h4>
      }
    >
      <CompactTable
        dataSource={monitorLogs}
        rowKey="name"
        columns={columns(t, user, setModal)}
        pagination={{
          current: page,
          showQuickJumper: true,
          showSizeChanger: true,
          total: monitorLogs.length,
          showTotal: (total: number, range: any[]) =>
            t('(RANGE1)-(RANGE2) of (TOTAL) items', {
              range1: range[0],
              range2: range[1],
              TOTAL: total,
            }),
        }}
        onChange={(pagination: any) => {
          if (page !== pagination.current) goToPage(pagination.current);
        }}
      />
      <JsonDetail
        record={modal}
        parentID={monitorId}
        closeModal={() => setModal(null)}
      ></JsonDetail>
    </PageContent>
  );
};

const columns = (
  t: TFunction,
  user: any,
  openDetailModal: any
): ColumnsType<MonitorLog> => [
  { width: 24 },
  {
    dataIndex: 'state',
    title: t('State'),
    render: (state: string) => (
      <StatusCircle
        // @ts-ignore
        size={8}
        text={{ data: String(state), hideUnavailable: true, italic: true }}
      />
    ),
  },
  {
    title: t('Message'),
    dataIndex: 'exception',
    key: 'exception',
    render: (message: string) => message || '',
  },
  {
    title: t('Date & Time'),
    dataIndex: 'created_at',
    key: 'created_at',
    sorter: (a: any, b: any) => objectArraySort(a, b, 'created_at'),
    render: (col: string) => {
      const created = dateFormat(col || '', DATE_FORMAT);
      const createdDiff = new Date().getTime() - new Date(col).getTime();
      const createdIn = humanizeDuration(createdDiff, {
        round: true,
        language: user.language,
        units: ['mo', 'd', 'h', 'm', 's'],
        largest: 1,
      });
      return (
        <div>
          <p className={'mb-0'} style={{ whiteSpace: 'nowrap' }}>
            {created}
          </p>
          <small className={'text-muted'}>
            {createdIn} {t('ago')}
          </small>
        </div>
      );
    },
  },
  {
    title: '',
    dataIndex: '',
    key: 'actions',
    width: 40,
    render: (record: any) => {
      return (
        record && (
          <Flex onClick={e => e.stopPropagation()}>
            <Tooltip title={t('Show details')}>
              <RowActionButton
                onClick={() => openDetailModal(record)}
                shape="circle"
                style={{ border: 'none', backgroundColor: 'transparent' }}
              >
                <Copy size={18} className="text-muted" />
              </RowActionButton>
            </Tooltip>
          </Flex>
        )
      );
    },
  },
];

export default MonitorLogs;
